import { useCallback, useState } from 'react';
import api from '@/lib/api';

/**
 * Putting a machine on a batch, in as few taps as the situation allows.
 *
 * One machine can run the stage, so there is no choice to make: it is assigned
 * on the spot and no sheet ever opens. Only a real choice gets a sheet, and the
 * sheet is a list of machines with what each is already carrying — the one fact
 * that decides it.
 *
 * Nothing here raises a dialog. A confirmation the supervisor has to dismiss is
 * one more tap on the most repeated action of their day.
 *
 * Mirrors `mobile-frontend/src/hooks/useAssignMachine.ts`.
 */
export function useAssignMachine(onAssigned) {
  const [target, setTarget] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const assign = useCallback(async (to, machine) => {
    setAssigning(true);
    let outcome;
    try {
      await api.post(`/production/operator-assignments/batches/${to.batchId}/assign`, {
        stage_name: to.stageName,
        machine_id: machine.id,
      });
      outcome = { ok: true, message: `${machine.machine_name || 'Machine'} assigned` };
    } catch (e) {
      outcome = {
        ok: false,
        message: e?.response?.data?.detail || e?.message || 'Could not assign that machine',
      };
    }
    setAssigning(false);
    setTarget(null);
    setMachines([]);
    onAssigned?.(outcome);
  }, [onAssigned]);

  const start = useCallback(async (to) => {
    if (!to.stageName) {
      onAssigned?.({ ok: false, message: 'This batch is not standing at a stage yet' });
      return;
    }
    setLoading(true);
    setTarget(to);
    let list = [];
    try {
      const res = await api.get(
        `/production/operator-assignments/stages/${encodeURIComponent(to.stageName)}/operators`,
      );
      list = res.data.machines || [];
    } catch {
      list = [];
    }
    setLoading(false);

    if (list.length === 1) {
      setTarget(null);
      await assign(to, list[0]);
      return;
    }
    if (list.length === 0) {
      setTarget(null);
      onAssigned?.({ ok: false, message: `No machine is linked to ${to.stageName}` });
      return;
    }
    setMachines(list);
  }, [assign, onAssigned]);

  const pick = useCallback((machine) => {
    if (target) assign(target, machine);
  }, [target, assign]);

  const cancel = useCallback(() => {
    setTarget(null);
    setMachines([]);
  }, []);

  return { target, machines, loading, assigning, start, pick, cancel };
}

export default useAssignMachine;
